import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Link,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ComposeMsgDialog from "./ComposeMsgDialog";
import SwapDialog from "./SwapDialog";
import AddAttendeeDialog from "./AddAttendeeDialog";
import AssignTicket from "../../../components/AssignTicket";
import { toast } from "../../../utilities/SnackBarUtils";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
import { MatchContext } from "../../../contexts/MatchContext";
import NewEventDialog from "../../../components/NewEventDialog/NewEventDialog";
import ScheduleMsgDialog from "./ScheduledMsgDialog";
import EventLogDialog from "./EventLogDialog";
import StaffingDialog from "../../../components/StaffingDialog/StaffingDialog";

import BulkTransferDialog from "./BulkTransferDialog";

const Actions = ({ refresh }) => {
  const navigate = useNavigate();
  const { pairingEvent } = useContext(EventContext);
  const { getData } = useContext(MatchContext);
  const [reminderConfirmOpen, setReminderConfirmOpen] = useState(false);
  const [matchConfirmOpen, setMatchConfirmOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [transerDialogOpen, setTransferDialogOpen] = useState(false);
  const [composeMsgOpen, setComposeMsgOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [assignInTNFM, setAssignInTNFM] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [staffingOpen, setStaffingOpen] = useState(false);
  const [eventLogOpen, setEventLogOpen] = useState(false);

  useEffect(() => {
    if (pairingEvent) {
      getEventChannels();
    }
  }, [pairingEvent]);

  const getEventChannels = async () => {
    const result = await request(
      `events/eventchannel/?eventId=${pairingEvent?.event?._id}`
    );
    if (!result.success) {
      toast.error(`Error retrieving event channels: ${result.error}`);
      return;
    }
    const tnfmChannel = result.data.filter(
      (c) => c.provider === "tnfm" && c.enabled === true
    );
    if (tnfmChannel.length > 0) {
      setAssignInTNFM(true);
      console.log("tnfm tickets");
    }
  };

  const handleReminder = async () => {
    setReminderConfirmOpen(false);
    const body = { eventId: pairingEvent?.event?._id };
    const result = await request(
      `pairingevent/incompleteprofilereminder`,
      "post",
      body
    );
    if (result.success) {
      toast.success(result.message);
    }
  };

  const handleMatches = async () => {
    setMatchConfirmOpen(false);
    const body = { pairingEventId: pairingEvent._id };
    const result = await request("pairingevent/sendmatchemails", "post", body);
    if (result.success) {
      toast.success("Match emails sent successfully!");
    }
  };

  const handleGoToEventPage = () => {
    if (pairingEvent?.event?.source === "tnfm") {
      window.open(
        pairingEvent?.event?.registrationLink,
        "_blank",
        "noreferrer"
      );
    } else {
      window.open(pairingEvent?.event?.eventbriteUrl, "_blank", "noreferrer");
    }
  };

  const handleDelete = async () => {
    setClearConfirmOpen(false);
    const body = { eventId: pairingEvent?.event?._id };
    const result = await request("pairing/deleteeventpairings", "post", body);
    if (result.success) {
      getData();
      toast.success("All pairings have been deleted");
    }
  };

  const handleDeleteEvent = async () => {
    const body = { eventId: pairingEvent?.event?._id };
    const result = await request("events/delete", "post", body);
    if (result.success) {
      toast.success("Event deleted.");
      navigate("/events");
    }
  };

  const handleExportAttendees = async () => {
    const result = await request(
      `reports/eventattendees/?eventId=${pairingEvent?.event?._id}`
    );
    if (result.success) {
      setDownloadLink(result.url);
    } else {
      toast.error(result.error);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item lg={3} md={4} sm={6} xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title="Messaging"
            sx={{ "& .MuiCardHeader-title": { fontSize: "1.3rem" } }}
          />
          <CardContent>
            <Stack spacing={3}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setReminderConfirmOpen(true)}
                disabled={
                  pairingEvent?.event?.status === "completed" ? true : false
                }
              >
                Send Profile Reminder
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setComposeMsgOpen(true)}
              >
                Compose Message
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setScheduledOpen(true)}
              >
                Scheduled Messages
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEventLogOpen(true)}
              >
                Event Comm Log
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMatchConfirmOpen(true)}
              >
                Send Match Emails
              </Button>
            </Stack>
          </CardContent>
          <Dialog
            open={reminderConfirmOpen}
            onClose={() => setReminderConfirmOpen(false)}
          >
            <DialogTitle id="alert-dialog-title">{"Confirm Send"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to send a reminder to attendees?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReminderConfirmOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleReminder} autoFocus>
                Send It
              </Button>
            </DialogActions>
          </Dialog>
          <ComposeMsgDialog
            open={composeMsgOpen}
            close={() => setComposeMsgOpen(false)}
            pairingEvent={pairingEvent}
          />
          <Dialog
            open={matchConfirmOpen}
            onClose={() => setMatchConfirmOpen(false)}
          >
            <DialogTitle id="alert-dialog-title">{"Confirm Send"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to send out the match emails?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMatchConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleMatches} autoFocus>
                Send It
              </Button>
            </DialogActions>
          </Dialog>
        </Card>
      </Grid>
      <Grid item lg={3} md={4} sm={6} xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title="Pairings & Attendees"
            sx={{ "& .MuiCardHeader-title": { fontSize: "1.3rem" } }}
          />
          <CardContent>
            <Stack spacing={3}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAddDialogOpen(true)}
              >
                Add Attendee
              </Button>
              <Button
                disabled={
                  pairingEvent?.event?.status === "scheduled" ||
                  pairingEvent?.event?.status === "pending"
                    ? false
                    : true
                }
                size="small"
                variant="outlined"
                onClick={() => setClearConfirmOpen(true)}
              >
                Clear Pairings
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setTransferDialogOpen(true)}
              >
                Transfer Attendees
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportAttendees}
              >
                Export Attendees
              </Button>
            </Stack>
          </CardContent>
          <Dialog
            open={clearConfirmOpen}
            onClose={() => setClearConfirmOpen(false)}
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirm Delete"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete all of the existing pairings?
                This cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleDelete} autoFocus>
                Clear them
              </Button>
            </DialogActions>
          </Dialog>
          <BulkTransferDialog
            open={transerDialogOpen}
            close={() => setTransferDialogOpen(false)}
          />
          {assignInTNFM ? (
            <AssignTicket
              open={addDialogOpen}
              close={() => {
                getData();
                setAddDialogOpen(false);
              }}
            />
          ) : (
            <AddAttendeeDialog
              open={addDialogOpen}
              close={() => {
                getData();
                setAddDialogOpen(false);
              }}
            />
          )}
        </Card>
      </Grid>
      <Grid item lg={3} md={4} sm={6} xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title="Other"
            sx={{ "& .MuiCardHeader-title": { fontSize: "1.3rem" } }}
          />
          <CardContent>
            <Stack spacing={3}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setStaffingOpen(true)}
              >
                Staffing
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleGoToEventPage}
                endIcon={<OpenInNewIcon />}
              >
                Go To Event Page
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setEditEventOpen(true)}
              >
                Edit event
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete Event
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <NewEventDialog
        open={editEventOpen}
        close={() => setEditEventOpen(false)}
        eventData={pairingEvent?.event}
      />
      <ScheduleMsgDialog
        open={scheduledOpen}
        close={() => setScheduledOpen(false)}
        pairingEventId={pairingEvent?._id}
      />
      <EventLogDialog
        open={eventLogOpen}
        close={() => setEventLogOpen(false)}
        eventId={pairingEvent?.event?._id}
      />
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Please Confirm</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this event?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEvent}>Delete it</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={downloadLink ? true : false}
        onClose={() => setDownloadLink(null)}
      >
        <DialogTitle>Click link to download file</DialogTitle>
        <DialogContent>
          <Link href={downloadLink}>Download</Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <StaffingDialog
        open={staffingOpen}
        close={() => setStaffingOpen(false)}
        eventId={pairingEvent?.event?._id}
      />
    </Grid>
  );
};

export default Actions;
